import React from 'react';
import { useFadeIn } from '../hooks/useFadeIn';

const ADVANTAGES = [
  {
    title: 'Climate Control',
    desc: 'Precise temperature & humidity management year-round',
    icon: <path d="M12 2a10 10 0 100 20A10 10 0 0012 2zm1 17.93V18a1 1 0 00-2 0v1.93A8 8 0 014.07 13H6a1 1 0 000-2H4.07A8 8 0 0111 4.07V6a1 1 0 002 0V4.07A8 8 0 0119.93 11H18a1 1 0 000 2h1.93A8 8 0 0113 19.93z"/>,
  },
  {
    title: 'Higher Yields',
    desc: 'Up to 3× higher crop output vs conventional farming',
    icon: <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>,
  },
  {
    title: 'Reduced Water Usage',
    desc: 'Smart drip irrigation cuts water use by up to 70%',
    icon: <path d="M12 2.69l5.66 5.66a8 8 0 11-11.31 0z"/>,
  },
  {
    title: 'Minimal Environmental Impact',
    desc: 'Reduced chemical inputs and zero soil erosion',
    icon: <path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>,
  },
];

export default function Greenhouse() {
  const ref = useFadeIn();

  return (
    <section id="greenhouse" className="py-24 px-6 bg-green-800 relative overflow-hidden">
      {/* Dot pattern overlay */}
      <div className="absolute inset-0 opacity-[0.04]"
        style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(255,255,255,.8) 1px, transparent 0)', backgroundSize: '40px 40px' }}
      />
      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center relative z-10" ref={ref}>
        {/* Left */}
        <div className="fade-in">
          <span className="inline-block text-xs font-semibold tracking-widest uppercase px-4 py-1.5 rounded-full mb-5 bg-green-400/12 text-green-300">
            Our Advantage
          </span>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-white leading-tight mb-5">
            The Greenhouse Advantage
          </h2>
          <p className="text-green-200/75 text-base leading-relaxed mb-8">
            Our modern greenhouse systems allow precise control of every environmental factor — delivering superior results season after season, regardless of external conditions.
          </p>
          <div className="flex flex-col gap-3">
            {ADVANTAGES.map(a => (
              <div key={a.title}
                className="flex items-start gap-4 bg-white/5 border border-white/10 rounded-xl px-5 py-4 transition-all duration-200 hover:bg-white/8 hover:border-green-400/30"
              >
                <div className="w-8 h-8 bg-green-400/15 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                  <svg className="w-4 h-4 fill-green-300" viewBox="0 0 24 24">{a.icon}</svg>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-white mb-0.5">{a.title}</h4>
                  <p className="text-xs text-green-300/70 leading-relaxed">{a.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right: Control Panel SVG */}
        <div className="fade-in">
          <div className="bg-white/4 border border-white/10 rounded-2xl p-10">
            <ControlPanelSVG />
          </div>
        </div>
      </div>
    </section>
  );
}

function ControlPanelSVG() {
  return (
    <svg viewBox="0 0 320 320" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto">
      <rect width="320" height="320" rx="12" fill="rgba(255,255,255,0.03)"/>
      <text x="160" y="28" textAnchor="middle" fill="#86efac" fontSize="11" fontFamily="DM Sans,sans-serif" fontWeight="600" letterSpacing="2">
        GREENHOUSE CONTROL
      </text>
      {/* Temp gauge */}
      <circle cx="90" cy="110" r="55" fill="none" stroke="rgba(74,222,128,0.1)" strokeWidth="8"/>
      <circle cx="90" cy="110" r="55" fill="none" stroke="#4ade80" strokeWidth="8"
        strokeDasharray="230 345" strokeDashoffset="86" strokeLinecap="round"/>
      <text x="90" y="104" textAnchor="middle" fill="white" fontSize="18" fontFamily="DM Sans,sans-serif" fontWeight="600">26°C</text>
      <text x="90" y="122" textAnchor="middle" fill="#6a9460" fontSize="9" fontFamily="DM Sans,sans-serif">Temperature</text>
      {/* Humidity gauge */}
      <circle cx="230" cy="110" r="55" fill="none" stroke="rgba(74,222,128,0.1)" strokeWidth="8"/>
      <circle cx="230" cy="110" r="55" fill="none" stroke="#86efac" strokeWidth="8"
        strokeDasharray="200 345" strokeDashoffset="86" strokeLinecap="round"/>
      <text x="230" y="104" textAnchor="middle" fill="white" fontSize="18" fontFamily="DM Sans,sans-serif" fontWeight="600">75%</text>
      <text x="230" y="122" textAnchor="middle" fill="#6a9460" fontSize="9" fontFamily="DM Sans,sans-serif">Humidity</text>
      <line x1="20" y1="190" x2="300" y2="190" stroke="rgba(255,255,255,0.08)" strokeWidth="1"/>
      {/* Stat boxes */}
      {[
        { x: 20,  label: '98%',  sub: 'Light Opt.' },
        { x: 120, label: '6.2',  sub: 'pH Level'   },
        { x: 220, label: 'EC 2.1', sub: 'Nutrients' },
      ].map(s => (
        <g key={s.sub}>
          <rect x={s.x} y="208" width="80" height="50" rx="8" fill="rgba(255,255,255,0.04)" stroke="rgba(74,222,128,0.15)" strokeWidth="1"/>
          <text x={s.x + 40} y="230" textAnchor="middle" fill="#4ade80" fontSize="14" fontFamily="DM Sans,sans-serif" fontWeight="600">{s.label}</text>
          <text x={s.x + 40} y="248" textAnchor="middle" fill="#6a9460" fontSize="8" fontFamily="DM Sans,sans-serif">{s.sub}</text>
        </g>
      ))}
      <circle cx="40" cy="285" r="5" fill="#4ade80"/>
      <text x="52" y="289" fill="#6a9460" fontSize="9" fontFamily="DM Sans,sans-serif">All systems optimal</text>
      <text x="300" y="289" textAnchor="end" fill="#3d5e3f" fontSize="9" fontFamily="DM Sans,sans-serif">LIVE</text>
    </svg>
  );
}
